import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
    collection, addDoc, setDoc, updateDoc, serverTimestamp, query, where, orderBy,
    limit as firestoreLimit, getDocs, deleteDoc, doc, getDoc, getDocFromServer,
    type DocumentSnapshot, type QueryDocumentSnapshot
} from 'firebase/firestore';
import { db, storage, auth } from '../config/firebase';
import { getCompanyProfileCached } from './cacheService';
import { getUserProfile } from './authService';
import { logger } from '../utils/logger';
import type {
    ReportMetadataPayload,
    SettingsPayload,
    ReportListItem,
    SavedSettingListItem,
    RevisionListItem,
    RevisionDetail,
    DetailWorkspaceStatus
} from './localServerService';
import { resolveSettingsExcelFile } from './localServerService';

const CLOUD_REPORTS_COLLECTION = 'cloudReports';
const CLOUD_SAVED_SETTINGS_COLLECTION = 'cloudSavedSettings';
const CLOUD_LIST_MAX_DOCS = 2000;
/** Extra docs fetched when operator-scoped recent list may skip others' rows. */
const CLOUD_RECENT_OPERATOR_OVERFETCH = 100;

/** Soft-hidden items older than this are hard-deleted. */
const HIDDEN_TTL_MS = 90 * 24 * 60 * 60 * 1000;
/** Cloud reports only: when total (incl. hidden) exceeds CAP, keep newest KEEP. */
const REPORTS_CAP = 500;
const REPORTS_KEEP = 250;

const RETENTION_TZ = 'Europe/Tallinn';
const RETENTION_WEEKDAY = 4; // Thursday
const RETENTION_HOUR = 21;
const RETENTION_MINUTE = 0;
const CLOUD_RETENTION_LAST_RUN_PREFIX = 'qd.cloudRetention.lastRunAt.';

function getTallinnParts(date: Date): {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    weekday: number;
} {
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: RETENTION_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        weekday: 'short'
    });
    const parts = dtf.formatToParts(date);
    const get = (type: Intl.DateTimeFormatPartTypes): string =>
        parts.find(p => p.type === type)?.value ?? '';
    const weekdayMap: Record<string, number> = {
        Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6
    };
    return {
        year: Number(get('year')),
        month: Number(get('month')),
        day: Number(get('day')),
        hour: Number(get('hour')),
        minute: Number(get('minute')),
        weekday: weekdayMap[get('weekday')] ?? -1
    };
}

function tallinnWallToUtcMs(
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number
): number {
    let guess = Date.UTC(year, month - 1, day, hour, minute, 0);
    for (let i = 0; i < 4; i++) {
        const parts = getTallinnParts(new Date(guess));
        const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0);
        const desired = Date.UTC(year, month - 1, day, hour, minute, 0);
        const delta = desired - asUtc;
        if (delta === 0) break;
        guess += delta;
    }
    return guess;
}

function getMostRecentThursday21TallinnMs(now = Date.now()): number {
    const p = getTallinnParts(new Date(now));
    const daysSinceThu = (p.weekday - RETENTION_WEEKDAY + 7) % 7;
    const cal = new Date(Date.UTC(p.year, p.month - 1, p.day));
    cal.setUTCDate(cal.getUTCDate() - daysSinceThu);

    let slot = tallinnWallToUtcMs(
        cal.getUTCFullYear(),
        cal.getUTCMonth() + 1,
        cal.getUTCDate(),
        RETENTION_HOUR,
        RETENTION_MINUTE
    );

    if (slot > now) {
        cal.setUTCDate(cal.getUTCDate() - 7);
        slot = tallinnWallToUtcMs(
            cal.getUTCFullYear(),
            cal.getUTCMonth() + 1,
            cal.getUTCDate(),
            RETENTION_HOUR,
            RETENTION_MINUTE
        );
    }
    return slot;
}

function isCloudRetentionDue(lastRunAt: number | null, now = Date.now()): boolean {
    const slot = getMostRecentThursday21TallinnMs(now);
    if (lastRunAt != null && lastRunAt >= slot) return false;
    return now >= slot;
}

function readCloudRetentionLastRun(companyId: string): number | null {
    try {
        const raw = localStorage.getItem(`${CLOUD_RETENTION_LAST_RUN_PREFIX}${companyId}`);
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
}

function writeCloudRetentionLastRun(companyId: string, lastRunAt: number): void {
    try {
        localStorage.setItem(`${CLOUD_RETENTION_LAST_RUN_PREFIX}${companyId}`, String(lastRunAt));
    } catch {
        // ignore quota / private mode
    }
}

function isCloudDocHidden(data: Record<string, unknown>): boolean {
    return typeof data.hiddenAt === 'number' && data.hiddenAt > 0;
}

function cloudTimestampMs(value: unknown): number {
    if (typeof value === 'number') return value;
    if (value && typeof value === 'object' && typeof (value as { toMillis?: () => number }).toMillis === 'function') {
        return (value as { toMillis: () => number }).toMillis();
    }
    return 0;
}

function includesSearchTerm(value: string | null | undefined, searchLower: string): boolean {
    return (value || '').toLowerCase().includes(searchLower);
}

function matchesCloudReportSearch(item: ReportListItem, searchLower: string): boolean {
    return (
        includesSearchTerm(item.detailNo, searchLower)
        || includesSearchTerm(item.orderNo, searchLower)
        || includesSearchTerm(item.operatorUid, searchLower)
        || includesSearchTerm(item.revisionLabel, searchLower)
    );
}

function matchesCloudSettingsSearch(item: SavedSettingListItem, searchLower: string): boolean {
    return (
        includesSearchTerm(item.detailNo, searchLower)
        || includesSearchTerm(item.revision, searchLower)
        || includesSearchTerm(item.createdByUid, searchLower)
        || includesSearchTerm(item.createdByName, searchLower)
    );
}

async function resolveOperatorScopeUid(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) return null;
    const profile = await getUserProfile(user.uid);
    const role = (profile?.role || '').toLowerCase();
    if (role === 'user') return user.uid;
    return null;
}

function sanitizeStorageFileName(name: string): string {
    return (name || 'file.pdf')
        .replace(/[/\\]/g, '_')
        .replace(/\s+/g, '_')
        .slice(0, 180);
}

async function resolveCompanyId(): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error('User is not authenticated');
    const profile = await getCompanyProfileCached(user.uid);
    return profile?.companyId || profile?.managerId || user.uid;
}

async function hardDeleteCloudReportDoc(d: QueryDocumentSnapshot): Promise<void> {
    const data = d.data();
    const storagePath = data.storagePath as string | undefined;
    if (storagePath) {
        try {
            await deleteObject(ref(storage, storagePath));
        } catch (e) {
            logger.warn('[CloudStorage] Could not delete report storage object:', e);
        }
    }
    await deleteDoc(d.ref);
}

async function hardDeleteCloudSettingsDoc(d: QueryDocumentSnapshot): Promise<void> {
    const data = d.data();
    const storagePath = data.storagePath as string | undefined;
    if (storagePath) {
        try {
            await deleteObject(ref(storage, storagePath));
        } catch (e) {
            logger.warn('[CloudStorage] Could not delete settings storage object:', e);
        }
    }
    await deleteDoc(d.ref);
}

async function runCloudReportsRetention(companyId: string): Promise<void> {
    const q = query(
        collection(db, CLOUD_REPORTS_COLLECTION),
        where('companyId', '==', companyId),
        orderBy('savedAt', 'desc'),
        firestoreLimit(CLOUD_LIST_MAX_DOCS)
    );
    const snap = await getDocs(q);
    const now = Date.now();
    const toDelete: QueryDocumentSnapshot[] = [];
    // All non-expired docs (visible + hidden < TTL) count toward the company cap.
    const keepCandidates: QueryDocumentSnapshot[] = [];

    for (const d of snap.docs) {
        const hiddenAt = d.data().hiddenAt;
        if (typeof hiddenAt === 'number' && now - hiddenAt >= HIDDEN_TTL_MS) {
            toDelete.push(d);
        } else {
            keepCandidates.push(d);
        }
    }

    if (keepCandidates.length > REPORTS_CAP) {
        toDelete.push(...keepCandidates.slice(REPORTS_KEEP));
    }

    for (const d of toDelete) {
        await hardDeleteCloudReportDoc(d);
    }

    if (toDelete.length > 0) {
        logger.log('[CloudStorage] Reports retention', {
            purged: toDelete.length,
            companyScoped: true
        });
    }
}

/** Cloud settings: hard-delete only when hidden longer than HIDDEN_TTL_MS. */
async function runCloudSettingsRetention(companyId: string): Promise<void> {
    const q = query(
        collection(db, CLOUD_SAVED_SETTINGS_COLLECTION),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc'),
        firestoreLimit(CLOUD_LIST_MAX_DOCS)
    );
    const snap = await getDocs(q);
    const now = Date.now();
    let purged = 0;

    for (const d of snap.docs) {
        const hiddenAt = d.data().hiddenAt;
        if (typeof hiddenAt === 'number' && now - hiddenAt >= HIDDEN_TTL_MS) {
            await hardDeleteCloudSettingsDoc(d);
            purged += 1;
        }
    }

    if (purged > 0) {
        logger.log('[CloudStorage] Settings retention', {
            purged,
            companyScoped: true
        });
    }
}

/**
 * Run cloud retention at most once per Thursday 21:00 Europe/Tallinn slot.
 * Catch-up when the PWA opens after the slot (no Cloud Functions scheduler).
 */
export async function ensureCloudRetentionIfDue(): Promise<void> {
    const companyId = await resolveCompanyId();
    const lastRunAt = readCloudRetentionLastRun(companyId);
    if (!isCloudRetentionDue(lastRunAt)) {
        return;
    }

    await runCloudReportsRetention(companyId);
    await runCloudSettingsRetention(companyId);
    writeCloudRetentionLastRun(companyId, Date.now());
    logger.log('[CloudStorage] Weekly retention completed', {
        timezone: RETENTION_TZ,
        schedule: 'Thu 21:00'
    });
}

/**
 * Upload an operator measurement report PDF to Firebase Storage and save
 * metadata to Firestore. Returns a ReportListItem-shaped object so the
 * call-site stays compatible with the local-server flow.
 */
export async function uploadReportToCloud(
    pdfBlob: Blob,
    metadata: ReportMetadataPayload
): Promise<ReportListItem> {
    const user = auth.currentUser;
    if (!user) throw new Error('User is not authenticated');

    const companyId = await resolveCompanyId();
    const safeName = sanitizeStorageFileName(metadata.fileName);
    const storagePath = `user-uploads/${user.uid}/cloud/reports/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, pdfBlob, { contentType: 'application/pdf' });
    const downloadUrl = await getDownloadURL(storageRef);

    const docData = {
        companyId,
        detailNo: metadata.detailNo,
        revisionId: metadata.revisionId ?? null,
        revisionLabel: metadata.revisionLabel ?? null,
        orderNo: metadata.orderNo ?? null,
        measurementNumber: metadata.measurementNumber ?? null,
        fileName: metadata.fileName,
        fileSizeBytes: pdfBlob.size,
        operatorUid: user.uid,
        operatorName: user.displayName ?? null,
        operatorEmail: user.email ?? null,
        operatorRole: metadata.operatorRole ?? null,
        storagePath,
        downloadUrl,
        savedAt: serverTimestamp(),
        values: metadata.values ?? null
    };
    const docRef = await addDoc(collection(db, CLOUD_REPORTS_COLLECTION), docData);

    logger.log('[CloudStorage] Report uploaded:', docRef.id);

    return {
        id: docRef.id,
        detailNo: metadata.detailNo,
        revisionId: metadata.revisionId ?? null,
        revisionLabel: metadata.revisionLabel ?? null,
        orderNo: metadata.orderNo ?? null,
        measurementNumber: metadata.measurementNumber ?? null,
        fileName: metadata.fileName,
        fileSizeBytes: pdfBlob.size,
        operatorUid: user.uid,
        operatorName: user.displayName ?? null,
        operatorEmail: user.email ?? null,
        operatorRole: metadata.operatorRole ?? null,
        savedAt: Date.now()
    };
}

/**
 * Upload editor settings PDF to Firebase Storage and save metadata to
 * Firestore. Mirrors the local-server uploadRevision flow.
 */
export async function uploadSettingsToCloud(
    detailNo: string,
    pdfBlob: Blob,
    settings: SettingsPayload,
    editorDisplayName?: string | null,
    revisionId?: string | null
): Promise<{ id: string }> {
    const user = auth.currentUser;
    if (!user) throw new Error('User is not authenticated');

    const companyId = await resolveCompanyId();
    const revisionLabel = settings.revision.trim();
    const trimmedDetail = detailNo.trim();
    const trimmedRevisionId = revisionId?.trim() || '';

    let existingDoc: DocumentSnapshot | null = null;

    if (trimmedRevisionId) {
        const snap = await getDoc(doc(db, CLOUD_SAVED_SETTINGS_COLLECTION, trimmedRevisionId));
        if (snap.exists()) {
            const data = snap.data();
            if (data.companyId === companyId && (data.detailNo as string) === trimmedDetail) {
                existingDoc = snap;
            }
        }
    }

    if (!existingDoc) {
        const existingQ = query(
            collection(db, CLOUD_SAVED_SETTINGS_COLLECTION),
            where('companyId', '==', companyId),
            where('detailNo', '==', trimmedDetail),
            where('revision', '==', revisionLabel),
            firestoreLimit(1)
        );
        const existingSnap = await getDocs(existingQ);
        existingDoc = existingSnap.docs[0] ?? null;
    }

    const safeDetail = sanitizeStorageFileName(detailNo);
    const safeRevision = sanitizeStorageFileName(`${revisionLabel}.pdf`);
    const storagePath =
        `user-uploads/${user.uid}/cloud/settings/${Date.now()}_${safeDetail}_${safeRevision}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, pdfBlob, { contentType: 'application/pdf' });
    const downloadUrl = await getDownloadURL(storageRef);

    const docData = {
        companyId,
        detailNo: trimmedDetail,
        revision: revisionLabel,
        settings,
        storagePath,
        downloadUrl,
        fileSizeBytes: pdfBlob.size,
        createdByUid: user.uid,
        createdByName: editorDisplayName?.trim() || user.displayName || null,
        status: 'published'
    };

    if (existingDoc) {
        const oldStoragePath = existingDoc.data().storagePath as string | undefined;
        await setDoc(existingDoc.ref, {
            ...docData,
            createdAt: existingDoc.data().createdAt ?? serverTimestamp(),
            hiddenAt: null
        });
        if (oldStoragePath && oldStoragePath !== storagePath) {
            try {
                await deleteObject(ref(storage, oldStoragePath));
            } catch (e) {
                logger.warn('[CloudStorage] Could not delete previous settings PDF:', e);
            }
        }
        logger.log('[CloudStorage] Settings updated:', existingDoc.id);
        return { id: existingDoc.id };
    }

    const docRef = await addDoc(collection(db, CLOUD_SAVED_SETTINGS_COLLECTION), {
        ...docData,
        createdAt: serverTimestamp(),
        hiddenAt: null
    });

    logger.log('[CloudStorage] Settings uploaded:', docRef.id);
    return { id: docRef.id };
}

export interface CloudReportListFilters {
    search?: string;
    limit?: number;
    offset?: number;
}

function mapCloudReportDoc(d: { id: string; data: () => Record<string, unknown> }): ReportListItem {
    const data = d.data();
    return {
        id: d.id,
        detailNo: (data.detailNo as string) ?? '',
        revisionId: (data.revisionId as string | null) ?? null,
        revisionLabel: (data.revisionLabel as string | null) ?? null,
        orderNo: (data.orderNo as string | null) ?? null,
        measurementNumber: (data.measurementNumber as string | null) ?? null,
        fileName: (data.fileName as string) ?? '',
        fileSizeBytes: (data.fileSizeBytes as number) ?? 0,
        operatorUid: (data.operatorUid as string) ?? '',
        operatorName: (data.operatorName as string | null) ?? null,
        operatorEmail: (data.operatorEmail as string | null) ?? null,
        operatorRole: (data.operatorRole as string | null) ?? null,
        savedAt: (data.savedAt as { toMillis?: () => number })?.toMillis?.()
            ?? (data.savedAt as number)
            ?? 0
    };
}

export async function listCloudReports(
    filters: CloudReportListFilters = {}
): Promise<{ items: ReportListItem[]; total: number }> {
    const searchTerm = (filters.search || '').trim();
    const companyId = await resolveCompanyId();
    const pageLimit = filters.limit ?? 50;
    const pageOffset = filters.offset ?? 0;
    const operatorScopeUid = await resolveOperatorScopeUid();

    // Recent (empty search): narrow Firestore read — not CLOUD_LIST_MAX_DOCS.
    if (!searchTerm) {
        const fetchLimit = operatorScopeUid
            ? Math.min(CLOUD_RECENT_OPERATOR_OVERFETCH, Math.max(pageLimit * 10, pageLimit))
            : Math.min(CLOUD_LIST_MAX_DOCS, Math.max(pageLimit * 5, pageLimit));
        const q = query(
            collection(db, CLOUD_REPORTS_COLLECTION),
            where('companyId', '==', companyId),
            orderBy('savedAt', 'desc'),
            firestoreLimit(fetchLimit)
        );
        const snap = await getDocs(q);
        const items: ReportListItem[] = [];
        for (const d of snap.docs) {
            if (isCloudDocHidden(d.data())) {
                continue;
            }
            const item = mapCloudReportDoc(d);
            if (operatorScopeUid && item.operatorUid !== operatorScopeUid) {
                continue;
            }
            items.push(item);
            if (items.length >= pageLimit) {
                break;
            }
        }
        return { items, total: items.length };
    }

    const searchLower = searchTerm.toLowerCase();
    const q = query(
        collection(db, CLOUD_REPORTS_COLLECTION),
        where('companyId', '==', companyId),
        orderBy('savedAt', 'desc'),
        firestoreLimit(CLOUD_LIST_MAX_DOCS)
    );

    const snap = await getDocs(q);
    const filtered: ReportListItem[] = [];

    for (const d of snap.docs) {
        if (isCloudDocHidden(d.data())) {
            continue;
        }
        const item = mapCloudReportDoc(d);
        if (operatorScopeUid && item.operatorUid !== operatorScopeUid) {
            continue;
        }
        if (!matchesCloudReportSearch(item, searchLower)) {
            continue;
        }
        filtered.push(item);
    }

    return {
        items: filtered.slice(pageOffset, pageOffset + pageLimit),
        total: filtered.length
    };
}

async function assertCloudReportAccess(reportId: string): Promise<{ downloadUrl: string; storagePath?: string }> {
    const companyId = await resolveCompanyId();
    const snap = await getDoc(doc(db, CLOUD_REPORTS_COLLECTION, reportId));
    if (!snap.exists()) throw new Error('Report not found');
    const data = snap.data();
    if (data.companyId !== companyId || isCloudDocHidden(data)) throw new Error('Report not found');
    return { downloadUrl: data.downloadUrl, storagePath: data.storagePath };
}

export async function fetchCloudReportPdfUrl(reportId: string): Promise<string> {
    const { downloadUrl } = await assertCloudReportAccess(reportId);
    return downloadUrl;
}

export async function deleteCloudReport(reportId: string): Promise<void> {
    const companyId = await resolveCompanyId();
    const reportRef = doc(db, CLOUD_REPORTS_COLLECTION, reportId);
    const snap = await getDoc(reportRef);
    if (!snap.exists()) throw new Error('Report not found');
    const data = snap.data();
    if (data.companyId !== companyId || isCloudDocHidden(data)) {
        throw new Error('Report not found');
    }
    await updateDoc(reportRef, { hiddenAt: Date.now() });
}

export async function deleteCloudSavedSettings(settingsId: string): Promise<void> {
    const companyId = await resolveCompanyId();
    const settingsRef = doc(db, CLOUD_SAVED_SETTINGS_COLLECTION, settingsId);
    const snap = await getDoc(settingsRef);
    if (!snap.exists()) throw new Error('Settings not found');
    const data = snap.data();
    if (data.companyId !== companyId || isCloudDocHidden(data)) {
        throw new Error('Settings not found');
    }
    await updateDoc(settingsRef, { hiddenAt: Date.now() });
}

function mapCloudSettingsDoc(d: { id: string; data: () => Record<string, unknown> }): SavedSettingListItem {
    const data = d.data();
    return {
        revisionId: d.id,
        detailNo: (data.detailNo as string) ?? '',
        revision: (data.revision as string) ?? '',
        status: (data.status as string) ?? 'published',
        createdByUid: (data.createdByUid as string) ?? '',
        createdByName: (data.createdByName as string | null) ?? null,
        createdAt: (data.createdAt as { toMillis?: () => number })?.toMillis?.()
            ?? (data.createdAt as number)
            ?? 0,
        fileSizeBytes: (data.fileSizeBytes as number) ?? 0
    };
}

export async function listCloudSavedSettings(
    filters: CloudReportListFilters = {}
): Promise<{ items: SavedSettingListItem[]; total: number }> {
    const searchTerm = (filters.search || '').trim();
    const companyId = await resolveCompanyId();
    const pageLimit = filters.limit ?? 50;
    const pageOffset = filters.offset ?? 0;

    // Recent (empty search): narrow Firestore read — not CLOUD_LIST_MAX_DOCS.
    if (!searchTerm) {
        const fetchLimit = Math.min(CLOUD_LIST_MAX_DOCS, Math.max(pageLimit * 5, pageLimit));
        const q = query(
            collection(db, CLOUD_SAVED_SETTINGS_COLLECTION),
            where('companyId', '==', companyId),
            orderBy('createdAt', 'desc'),
            firestoreLimit(fetchLimit)
        );
        const snap = await getDocs(q);
        const items: SavedSettingListItem[] = [];
        for (const d of snap.docs) {
            if (isCloudDocHidden(d.data())) {
                continue;
            }
            items.push(mapCloudSettingsDoc(d));
            if (items.length >= pageLimit) {
                break;
            }
        }
        return { items, total: items.length };
    }

    const searchLower = searchTerm.toLowerCase();
    const q = query(
        collection(db, CLOUD_SAVED_SETTINGS_COLLECTION),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc'),
        firestoreLimit(CLOUD_LIST_MAX_DOCS)
    );

    const snap = await getDocs(q);
    const filtered: SavedSettingListItem[] = [];

    for (const d of snap.docs) {
        if (isCloudDocHidden(d.data())) {
            continue;
        }
        const item = mapCloudSettingsDoc(d);
        if (!matchesCloudSettingsSearch(item, searchLower)) {
            continue;
        }
        filtered.push(item);
    }

    return {
        items: filtered.slice(pageOffset, pageOffset + pageLimit),
        total: filtered.length
    };
}

export async function fetchCloudSettingsPdfUrl(settingsId: string): Promise<string> {
    const companyId = await resolveCompanyId();
    const snap = await getDoc(doc(db, CLOUD_SAVED_SETTINGS_COLLECTION, settingsId));
    if (!snap.exists()) throw new Error('Settings not found');
    const data = snap.data();
    if (data.companyId !== companyId || isCloudDocHidden(data)) throw new Error('Settings not found');
    return data.downloadUrl;
}

/**
 * List published revisions for a detail number from Cloud DB (Firestore).
 * Returns items in the same shape as the local-server listRevisions API.
 */
export async function listCloudRevisionsForDetail(detailNo: string): Promise<RevisionListItem[]> {
    const trimmed = detailNo.trim();
    if (!trimmed) return [];

    const companyId = await resolveCompanyId();
    const q = query(
        collection(db, CLOUD_SAVED_SETTINGS_COLLECTION),
        where('companyId', '==', companyId),
        where('detailNo', '==', trimmed),
        where('status', '==', 'published')
    );
    const snap = await getDocs(q);
    const items: RevisionListItem[] = snap.docs
        .filter((d) => !isCloudDocHidden(d.data()))
        .map((d) => {
            const data = d.data();
            const settings = data.settings as SettingsPayload | undefined;
            return {
                id: d.id,
                revision: (data.revision as string) ?? '',
                status: 'published' as const,
                sequenceMeasurement: settings?.sequenceMeasurement ?? false,
                examMode: settings?.examMode ?? false,
                createdByName: (data.createdByName as string | null) ?? null,
                createdAt: cloudTimestampMs(data.createdAt)
            };
        });
    items.sort((a, b) => a.createdAt - b.createdAt);
    return items;
}

export async function getCloudDetailWorkspaceStatus(detailNo: string): Promise<DetailWorkspaceStatus> {
    const items = await listCloudRevisionsForDetail(detailNo);
    return { registered: items.length > 0 };
}

/**
 * Fetch a cloud-stored revision as File objects for PdfViewer (same contract as
 * local-server fetchRevisionAsFiles).
 */
export async function fetchCloudRevisionAsFiles(
    detailNo: string,
    revisionId: string
): Promise<{ pdfFile: File; excelFile: File; revision: RevisionDetail }> {
    const companyId = await resolveCompanyId();
    const trimmedDetail = detailNo.trim();
    const snap = await getDocFromServer(doc(db, CLOUD_SAVED_SETTINGS_COLLECTION, revisionId));
    if (!snap.exists()) {
        throw new Error('Settings not found');
    }
    const data = snap.data();
    if (data.companyId !== companyId || (data.detailNo as string) !== trimmedDetail || isCloudDocHidden(data)) {
        throw new Error('Settings not found');
    }

    const settings = data.settings as SettingsPayload;
    const revisionLabel = (data.revision as string) ?? '';
    const pdfResponse = await fetch(data.downloadUrl as string, { cache: 'no-store' });
    if (!pdfResponse.ok) {
        throw new Error(`Failed to download PDF (HTTP ${pdfResponse.status})`);
    }
    const pdfBlob = await pdfResponse.blob();
    const safeName = `${trimmedDetail}_${revisionLabel}.pdf`.replace(/[^A-Za-z0-9._-]/g, '_');
    const pdfFile = new File([pdfBlob], safeName, { type: 'application/pdf' });
    const normalizedSettings: SettingsPayload = {
        ...settings,
        examMode: settings.examMode ?? false,
    };
    const excelFile = await resolveSettingsExcelFile(pdfFile, normalizedSettings);

    const revision: RevisionDetail = {
        id: revisionId,
        detailNo: trimmedDetail,
        revision: revisionLabel,
        status: 'published',
        sequenceMeasurement: normalizedSettings.sequenceMeasurement,
        examMode: normalizedSettings.examMode,
        createdByName: (data.createdByName as string | null) ?? null,
        createdAt: data.createdAt?.toMillis?.() ?? (data.createdAt as number) ?? 0,
        settings: normalizedSettings
    };

    logger.log('[CloudStorage] Loaded revision', {
        detailNo: trimmedDetail,
        revisionId,
        revision: revisionLabel,
        rects: settings.rects.length
    });

    return { pdfFile, excelFile, revision };
}
